import React from "react";
import { Badge, Text } from "@chakra-ui/react";
import { CrudPage } from "../components/CrudPage";
import { transactions } from "../services/api";
import { TableFilterDefinition } from "../types";

const formatRial = (value?: number) =>
  value ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const formatCardLike = (value?: string | null) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "—";
  return normalized.replace(/(.{4})(?=.)/g, "$1-");
};

const statusMeta: Record<string, { label: string; color: string }> = {
  initiated: { label: "شروع شده", color: "gray" },
  token_received: { label: "آماده پرداخت", color: "blue" },
  paid: { label: "پرداخت موفق", color: "green" },
  failed: { label: "ناموفق", color: "red" },
  cancelled: { label: "لغو شده", color: "orange" },
};

const formatInvestorLabel = (investor?: any) => {
  const fullName = String(investor?.fullName || "").trim();
  const nationalId = String(investor?.nationalId || "").trim();

  if (fullName && nationalId) {
    return `${fullName} - ${nationalId}`;
  }

  return fullName || nationalId || "—";
};

const paymentMethodMeta: Record<string, string> = {
  gateway: "درگاه پرداخت",
  card: "کارت هدیه",
  slip: "فیش انتقال",
};

const columns = [
  {
    header: "سرمایه‌گذار",
    accessor: "request.investor",
    render: (item: any) => formatInvestorLabel(item?.request?.investor),
  },
  {
    header: "صندوق",
    accessor: "fundKey",
    isSortable: true,
    render: (item: any) =>
      item.request?.fundName ||
      item.request?.fundSeoRegisterNo ||
      item.fundKey ||
      "—",
  },
  {
    header: "مبلغ",
    accessor: "amountRial",
    isSortable: true,
    render: (item: any) => formatRial(item.amountRial),
  },
  {
    header: "وضعیت",
    accessor: "status",
    isSortable: true,
    render: (item: any) => {
      const meta = statusMeta[item.status] || {
        label: item.status || "—",
        color: "gray",
      };
      return <Badge colorScheme={meta.color}>{meta.label}</Badge>;
    },
  },
  {
    header: "روش پرداخت",
    accessor: "request.paymentMethod",
    render: (item: any) =>
      paymentMethodMeta[item.request?.paymentMethod] || "—",
  },
  {
    header: "کارت",
    accessor: "cardMask",
    isSortable: true,
    render: (item: any) => (
      <Text dir="ltr" style={{ unicodeBidi: "plaintext" }}>
        {formatCardLike(item.cardMask)}
      </Text>
    ),
  },
  {
    header: "تاریخ",
    accessor: "createdAt",
    isSortable: true,
    render: (item: any) => formatDate(item.createdAt),
  },
];

const transactionFilterDefinitions: TableFilterDefinition[] = [
  {
    key: "fundKey",
    label: "شناسه صندوق",
    type: "text",
    placeholder: "شناسه صندوق",
  },
  {
    key: "status",
    label: "وضعیت",
    type: "select",
    options: Object.entries(statusMeta).map(([value, meta]) => ({
      value,
      label: meta.label,
    })),
  },
  {
    key: "gateway",
    label: "درگاه تراکنش",
    type: "select",
    options: [
      { value: "parsian", label: "درگاه پارسیان" },
      { value: "payment_card", label: "کارت هدیه" },
    ],
  },
  {
    key: "amountMin",
    label: "حداقل مبلغ (ریال)",
    type: "number",
    placeholder: "از مبلغ",
    useThousandsSeparator: true,
  },
  {
    key: "amountMax",
    label: "حداکثر مبلغ (ریال)",
    type: "number",
    placeholder: "تا مبلغ",
    useThousandsSeparator: true,
  },
  {
    key: "createdFrom",
    label: "تاریخ تراکنش از",
    type: "date",
    placeholder: "تاریخ شروع",
    usePersianDatePicker: true,
  },
  {
    key: "createdTo",
    label: "تاریخ تراکنش تا",
    type: "date",
    placeholder: "تاریخ پایان",
    usePersianDatePicker: true,
  },
];

export const TransactionsPage: React.FC = () => {
  return (
    <CrudPage
      title="تراکنش‌های امید"
      columns={columns}
      queryKey="transactions"
      resourceName="transactions"
      api={transactions}
      showAddButton={false}
      enableEdit={false}
      enableDelete={false}
      enableView={false}
      FormComponent={() => null}
      filterDefinitions={transactionFilterDefinitions}
      initialSort={{ field: "createdAt", direction: "desc" }}
    />
  );
};
